import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { url } from "../constants/url";
import { categories } from "../constants/constants";

const DetailPageProfessional = () => {
  const { id } = useParams();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const { data } = await axios.get(
          `${url}/api/admin/profile/professional/${id}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setProfile(data.profile);
      } catch (error) {
        // if unauthorized, clear auth and redirect
        if (error.response?.status === 401) {
          localStorage.clear();
          navigate("/login");
        } else {
          console.error("Error fetching profile:", error);
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="p-4 md:p-6 bg-white rounded-3xl shadow-md relative">
        <Skeleton height={250} />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="p-4 md:p-6">
        Error loading profile. Please try again later.
      </div>
    );
  }

  const info = profile.UserInformation || {};
  const salary = info.SalaryExps?.[0] || {};
  const portfolioImages = info.portfolio?.split(",").filter(Boolean) || [];

  // Positions grouping
  const positionIds =
    info.positionsOpen?.split(",").map(Number).filter(Boolean) || [];
  const groupedPositions = groupItems(positionIds);

  // Services grouping
  const serviceIds =
    info.categoriesId?.split(",").map(Number).filter(Boolean) || [];
  const groupedServices = groupItems(serviceIds);

  // Working days formatting
  const shortWeekdays = {
    Monday: "Mon",
    Tuesday: "Tue",
    Wednesday: "Wed",
    Thursday: "Thu",
    Friday: "Fri",
    Saturday: "Sat",
    Sunday: "Sun"
  };
  const workingDaysDisplay = (salary.workingDays || "")
    .split(",")
    .map((d) => shortWeekdays[d.trim()] || d.trim())
    .join(" , ");

  // Generic grouping function for positions & services
  function groupItems(ids) {
    const items = ids
      .map((id) => {
        const parent = categories.find((c) => c.id === id);
        if (parent)
          return {
            parentId: parent.id,
            parentName: parent.name,
            subName: null
          };
        for (const c of categories) {
          const sub = c.subcategories.find((s) => s.id === id);
          if (sub)
            return { parentId: c.id, parentName: c.name, subName: sub.name };
        }
        return null;
      })
      .filter(Boolean);

    return items.reduce((acc, { parentId, parentName, subName }) => {
      if (!acc[parentId]) acc[parentId] = { parentName, subs: [] };
      if (subName && !acc[parentId].subs.includes(subName))
        acc[parentId].subs.push(subName);
      return acc;
    }, {});
  }

  // Download CV
  const downloadCV = () => {
    const cvUrl = info.cvUrl;
    if (!cvUrl) return alert("CV not available");
    const a = document.createElement("a");
    a.href = cvUrl;
    a.download = "cv.pdf";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="p-4 md:p-6 bg-white rounded-3xl shadow-md relative">
      {/* Header */}
      <div className="flex flex-col items-center text-center mb-6">
        <img
          src={info.profile || "https://via.placeholder.com/150"}
          alt={profile.fullName}
          className="w-40 h-40 rounded-full mb-4 shadow-lg object-cover"
        />
        <h1 className="text-2xl md:text-3xl font-semibold mb-1">
          {profile.fullName}
        </h1>
        <p className="text-gray-600 mb-1">
          {info.city}, {info.state}
        </p>
        <p className="text-gray-500 text-sm">{profile.email}</p>
      </div>

      {/* Download CV */}
      <div className="flex justify-center mb-6">
        <button
          onClick={downloadCV}
          className="p-2 bg-[#AF9C80] text-white rounded-full"
        >
          Download CV
        </button>
      </div>

      {/* About */}
      <section className="mb-6">
        <h2 className="text-xl font-semibold mb-2">About</h2>
        <p>{info.about || "No about provided."}</p>
      </section>

      {/* Professions & Expertise */}
      {Object.keys(groupedPositions).length > 0 && (
        <section className="mb-6 bg-gray-100 p-4 rounded-lg">
          <h2 className="text-xl font-semibold mb-3">
            Professions & Expertise
          </h2>
          {Object.values(groupedPositions).map((group) => (
            <div key={group.parentName} className="mb-4">
              <h3 className="text-lg font-medium mb-1">{group.parentName}</h3>
              <ul className="list-disc list-inside ml-4">
                {group.subs.length > 0 ? (
                  group.subs.map((sub) => <li key={sub}>{sub}</li>)
                ) : (
                  <li>General</li>
                )}
              </ul>
            </div>
          ))}
        </section>
      )}

      {/* Services We Offer */}
      {Object.keys(groupedServices).length > 0 && (
        <section className="mb-6 bg-gray-100 p-4 rounded-lg">
          <h2 className="text-xl font-semibold mb-3">Profession & Expertise</h2>
          {Object.values(groupedServices).map((group) => (
            <div key={group.parentName} className="mb-4">
              <h3 className="text-lg font-medium mb-1">{group.parentName}</h3>
              <ul className="list-disc list-inside ml-4">
                {group.subs.length > 0 ? (
                  group.subs.map((sub) => <li key={sub}>{sub}</li>)
                ) : (
                  <li>General</li>
                )}
              </ul>
            </div>
          ))}
        </section>
      )}
      {/* Compensation */}
      <h2 className="text-xl font-semibold mb-2">Salary Expectation</h2>
      <section className="mb-6 bg-gray-200 p-4 rounded-lg grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-4 bg-white border rounded-lg">
          <strong>Booth Rent:</strong> Not more than ${salary.boothRent}/wk
        </div>
        <div className="p-4 bg-white border rounded-lg">
          <strong>Commission:</strong> Not less than {salary.commission}%
        </div>
        <div className="p-4 bg-white border rounded-lg">
          <strong>Salary:</strong> Not less than ${salary.salary}/Yr
        </div>
        <div className="p-4 bg-white border rounded-lg">
          <strong>Hourly Rent:</strong> Not less than ${salary.hourly}/hr
        </div>
        <div className="p-4 bg-white border rounded-lg">
          <strong>Lease Fee:</strong> Not more than ${salary.leaseFee}/Mo
        </div>
      </section>
      {/* Working Days */}
      {workingDaysDisplay && (
        <section className="mb-6 bg-gray-100 p-4 rounded-lg">
          <h2 className="text-xl font-semibold mb-2">Working Availability</h2>
          <p className="p-4 bg-white border rounded-lg">{workingDaysDisplay}</p>
          <div className="mt-2 grid grid-cols-2 gap-4">
            <div className="p-4 bg-white border rounded-lg">
              <strong>Start Time:</strong> {salary.startTime}
            </div>
            <div className="p-4 bg-white border rounded-lg">
              <strong>End Time:</strong> {salary.endTime}
            </div>
          </div>
        </section>
      )}

      {/* Portfolio */}
      <section className="mb-20">
        <h2 className="text-xl font-semibold mb-2">Portfolio</h2>
        {portfolioImages.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {portfolioImages.map((img, idx) => (
              <img
                key={idx}
                src={img}
                alt={`Portfolio ${idx + 1}`}
                className="w-full h-40 object-cover rounded-md cursor-pointer hover:opacity-90"
                onClick={() => setSelectedImage(img)}
              />
            ))}
          </div>
        ) : (
          <p>No portfolio images available.</p>
        )}
      </section>

      {/* Image Modal */}
      {/* {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="relative bg-white p-4 rounded-2xl shadow-2xl max-w-3xl w-full mx-4 md:mx-0"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-2 right-4 text-[#AF9C80] text-3xl font-bold hover:text-black"
            >
              &times;
            </button>
            <img
              src={selectedImage}
              alt="Portfolio Large"
              className="w-full h-auto rounded-b-2xl"
            />
          </div>
        </div>
      )} */}

      {selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50"
          onClick={() => setSelectedImage(null)}
        >
          <div
            className="relative bg-white p-4 rounded-2xl shadow-xl max-w-3xl w-full mx-4 md:mx-0"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-2 right-4 text-[#AF9C80] text-3xl font-bold hover:text-black"
            >
              &times;
            </button>
            <img
              src={selectedImage}
              alt="Full View"
              className="w-full h-auto rounded-b-2xl"
            />
          </div>
        </div>
      )}

      {/* Footer Actions */}
      {/* <div className="sm:absolute bottom-4 right-4 flex space-x-2">
        <button className="p-2 bg-green-500 w-40 text-white rounded-full">
          Block User
        </button>
        <button className="p-2 bg-yellow-500 w-40 text-white rounded-full">
          Suspend User
        </button>
      </div> */}
    </div>
  );
};

export default DetailPageProfessional;
